import type {JSONContent} from "@tiptap/core";
import {DEFAULT_PRINTOUT_CONTENT} from "../core/printoutDocument";
import {
    resolvePrintoutVariable,
    type PrintoutVariableResolveContext,
} from "../variables/printoutVariableResolver";
import {
    findPrintoutVariable,
    normalizePrintoutDateFormat,
    normalizePrintoutVariableFallback,
} from "../variables/printoutVariables";
import type {PrintoutVariableFormatting} from "../variables/printoutVariables";
import {
    FOLIO_ITEM_DATE_FIELDS,
    FOLIO_NET_TOTAL_KEY,
    FOLIO_TAX_COLUMN_KEYS,
    FOLIO_TAX_TOTAL_KEY,
    getFolioTableAttrs,
    isSyntheticFolioItem,
} from "../blocks/folioTable/printoutFolioTableOptions";
import {getDataListAttrs} from "../blocks/dataList/printoutDataListOptions";
import type {
    DataListSource,
    PrintoutDataListAttrs,
} from "../blocks/dataList/printoutDataListOptions";
import type {
    FolioResolvedSection,
    PrintoutFolioTableAttrs,
} from "../blocks/folioTable/printoutFolioTableOptions";

const EMPTY_DOCUMENT = JSON.parse(DEFAULT_PRINTOUT_CONTENT) as JSONContent;

export const RESOLVED_PRINTOUT_VARIABLE_MARK = "resolvedPrintoutVariable";

const getPrintoutVariableReference = (attrs?: Record<string, unknown>) => {
    const key = typeof attrs?.key === "string" ? attrs.key : "";
    const language = typeof attrs?.language === "string" ? attrs.language : "";

    if (!key) return "";

    return language ? `${key}:${language}` : key;
};

const getPrintoutVariableFormatting = (
    attrs?: Record<string, unknown>,
): PrintoutVariableFormatting => ({
    dateFormat: normalizePrintoutDateFormat(attrs?.dateFormat),
});

const VISUAL_PRINTOUT_VARIABLE_MARKS = new Set(["highlight", "textStyle"]);

const getResolvedPrintoutVariableMarks = (
    marks: JSONContent["marks"],
    resolvedVariable: ReturnType<typeof resolvePrintoutVariable>,
) => [
    ...(marks?.filter(mark => !VISUAL_PRINTOUT_VARIABLE_MARKS.has(mark.type)) ?? []),
    {
        type: RESOLVED_PRINTOUT_VARIABLE_MARK,
        attrs: {
            key: resolvedVariable?.key ?? null,
            nowrap: resolvedVariable?.variable.attr.nowrap === true,
        },
    },
];

const resolvePrintoutVariableCell = (
    reference: string,
    context: PrintoutVariableResolveContext,
    formatting?: PrintoutVariableFormatting,
): {resolved: boolean; value: string} => {
    const resolvedVariable = resolvePrintoutVariable(reference, context, formatting);
    if (resolvedVariable?.displayValue) return {resolved: true, value: resolvedVariable.displayValue};

    const variableKey = reference.split(":")[0];
    const variable = resolvedVariable?.variable ?? findPrintoutVariable(variableKey);
    if (!variable) return {resolved: false, value: ""};

    return {
        resolved: false,
        value: normalizePrintoutVariableFallback(undefined, variable),
    };
};

const resolvePrintoutVariableText = (
    reference: string,
    context: PrintoutVariableResolveContext,
    formatting?: PrintoutVariableFormatting,
    fallbackWhenEmpty = true,
) => {
    const resolved = resolvePrintoutVariable(reference, context, formatting);
    if (resolved?.displayValue || fallbackWhenEmpty) {
        return resolvePrintoutVariableCell(reference, context, formatting).value;
    }

    return "";
};

type ResolvedPrintoutNode = JSONContent | JSONContent[] | null;
type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => (
    typeof value === "object" && value !== null && !Array.isArray(value)
);

const getDataListRows = (
    source: DataListSource,
    context: PrintoutVariableResolveContext,
    attrs?: PrintoutDataListAttrs,
) => {
    switch (source) {
        case "folioItems":
            return (context.folioItems ?? []).filter(isRecord);
        case "guestDietaryRequirements":
            return (context.reservationProfiles ?? []).filter(isRecord).filter(profile => (
                ["guestAllergies", "guestDietaryPreferences"].some(key => (
                    resolvePrintoutVariable(key, {...context, profile})?.isEmpty === false
                ))
            ));
        case "agendaItems": {
            const rows = (context.agendaItems ?? []).filter(isRecord);
            const sectorTypes = attrs?.sectorTypes ?? [];
            if (!sectorTypes.length) return rows;

            return rows.filter(row => (
                typeof row.sectorType === "string"
                && (sectorTypes as readonly string[]).includes(row.sectorType)
            ));
        }
        case "reservationMealPeriods":
        default:
            return (context.reservationMealPeriods ?? []).filter(isRecord);
    }
};

const getDataListRowContext = (
    source: DataListSource,
    row: UnknownRecord,
    context: PrintoutVariableResolveContext,
): PrintoutVariableResolveContext => {
    switch (source) {
        case "folioItems":
            return {
                ...context,
                folioItem: row,
            };
        case "guestDietaryRequirements":
            return {
                ...context,
                profile: row,
            };
        case "agendaItems":
            return {
                ...context,
                agendaItem: row,
            };
        case "reservationMealPeriods": {
            const sectorResources = Array.isArray(row.sectorResources)
                ? row.sectorResources.filter(isRecord)
                : [];
            const rowReservation = isRecord(row.reservation) ? row.reservation : undefined;
            const rowRestaurant = isRecord(row.restaurant) ? row.restaurant : undefined;
            const rowResource = isRecord(row.resource)
                ? row.resource
                : sectorResources[0];

            return {
                ...context,
                reservation: rowReservation,
                resource: rowResource,
                restaurant: rowRestaurant,
                sectorResources,
            };
        }
        default:
            return context;
    }
};

const getSortableNumber = (value: string) => {
    const trimmedValue = value.trim();
    if (!/\d/.test(trimmedValue)) return undefined;

    const compactValue = trimmedValue.replace(/\s/g, "").replace(/[^\d,.-]/g, "");
    const lastComma = compactValue.lastIndexOf(",");
    const lastDot = compactValue.lastIndexOf(".");
    const decimalSeparator = lastComma > lastDot ? "," : ".";
    const normalizedValue = compactValue
        .replace(new RegExp(`\\${decimalSeparator === "," ? "." : ","}`, "g"), "")
        .replace(decimalSeparator, ".");
    const parsedValue = Number.parseFloat(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

interface ResolvedFolioRow {
    sourceIndex: number;
    values: Record<string, string>;
    resolvedColumnIds: Set<string>;
    inclusive: boolean;
    timeSpanStart?: number;
    timeSpanEnd?: number;
    dayKey: string;
    dayLabel: string;
}

const toFolioDate = (value: unknown): Date | undefined => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    if (typeof value === "string" || typeof value === "number") {
        const date = new Date(value);

        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    return undefined;
};

// Group by the guest's local calendar day, not an ISO instant.
const getFolioDayKey = (date?: Date) => (
    date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : ""
);

// Chronological sorts use raw dates; missing dates remain last.
const sortResolvedFolioRows = (
    rows: ResolvedFolioRow[],
    attrs: PrintoutFolioTableAttrs,
) => {
    const sortColumn = attrs.sort.columnId
        ? attrs.columns.find(column => column.id === attrs.sort.columnId)
        : undefined;
    const sortDateField = sortColumn ? FOLIO_ITEM_DATE_FIELDS[sortColumn.variableKey] : undefined;
    const multiplier = attrs.sort.direction === "desc" ? -1 : 1;

    if (!sortColumn || sortDateField || attrs.groupByDate) {
        const field = sortDateField ?? "timeSpanStart";

        return [...rows].sort((first, second) => {
            const firstDate = first[field];
            const secondDate = second[field];

            if (firstDate === undefined && secondDate === undefined) return first.sourceIndex - second.sourceIndex;
            if (firstDate === undefined) return 1;
            if (secondDate === undefined) return -1;

            return firstDate === secondDate
                ? first.sourceIndex - second.sourceIndex
                : (firstDate - secondDate) * multiplier;
        });
    }

    const sortColumnId = sortColumn.id;

    return [...rows].sort((first, second) => {
        const firstValue = first.values[sortColumnId] ?? "";
        const secondValue = second.values[sortColumnId] ?? "";
        const firstNumber = getSortableNumber(firstValue);
        const secondNumber = getSortableNumber(secondValue);
        const comparedValue = firstNumber !== undefined && secondNumber !== undefined
            ? firstNumber - secondNumber
            : firstValue.localeCompare(secondValue, undefined, {numeric: true, sensitivity: "base"});

        return comparedValue === 0
            ? first.sourceIndex - second.sourceIndex
            : comparedValue * multiplier;
    });
};

const buildResolvedFolioGroups = (rows: ResolvedFolioRow[]): FolioResolvedSection[] | null => {
    const sections: FolioResolvedSection[] = [];
    let currentKey: string | undefined;

    rows.forEach((row, index) => {
        if (row.dayKey !== currentKey) {
            sections.push({label: row.dayKey ? row.dayLabel : "", rowCount: 0, startIndex: index});
            currentKey = row.dayKey;
        }

        sections[sections.length - 1].rowCount += 1;
    });

    return sections.some(section => section.label) ? sections : null;
};

// Filter rollups, keep inclusive rows outside sums, and hide unsupported tax output.
const resolvePrintoutFolioTableNode = (
    node: JSONContent,
    context: PrintoutVariableResolveContext,
): JSONContent => {
    const attrs = getFolioTableAttrs(node.attrs as PrintoutFolioTableAttrs);

    const sourceItems = (context.folioItems ?? []).filter(isRecord);
    const items = attrs.excludeSyntheticRows
        ? sourceItems.filter(item => !isSyntheticFolioItem(item))
        : sourceItems;
    const totalsContext: PrintoutVariableResolveContext = items.length === sourceItems.length
        ? context
        : {...context, folioItems: items};

    const rows = sortResolvedFolioRows(
        items.map((folioItem, sourceIndex) => {
            const rowContext: PrintoutVariableResolveContext = {...context, folioItem};
            const resolvedColumnIds = new Set<string>();
            const values: Record<string, string> = {};

            attrs.columns.forEach((column) => {
                const cell = resolvePrintoutVariableCell(column.variableKey, rowContext);
                if (cell.resolved) resolvedColumnIds.add(column.id);
                values[column.id] = cell.value;
            });

            const startDate = toFolioDate(folioItem.timeSpanStart);
            const endDate = toFolioDate(folioItem.timeSpanEnd);

            return {
                dayKey: getFolioDayKey(startDate),
                dayLabel: startDate ? resolvePrintoutVariableText("folioItemStartDate", rowContext) : "",
                inclusive: folioItem.inclusive === true,
                resolvedColumnIds,
                sourceIndex,
                timeSpanEnd: endDate?.getTime(),
                timeSpanStart: startDate?.getTime(),
                values,
            };
        }),
        attrs,
    );

    const hiddenColumnIds = attrs.columns
        .filter(column => (
            FOLIO_TAX_COLUMN_KEYS.has(column.variableKey)
            // Empty data is not evidence that a configured column is unsupported.
            && rows.length > 0
            && rows.every(row => !row.resolvedColumnIds.has(column.id))
        ))
        .map(column => column.id);

    const totalResolutions = new Map(attrs.totals.map(total => [
        total.id,
        resolvePrintoutVariableCell(total.variableKey, totalsContext),
    ]));
    // Avoid implying a tax position by printing only one half of a configured net/VAT pair.
    const pairKeys = [FOLIO_NET_TOTAL_KEY, FOLIO_TAX_TOTAL_KEY];
    const pairRows = attrs.totals.filter(total => pairKeys.includes(total.variableKey));
    const pairIsConfigured = pairKeys.every(key => pairRows.some(total => total.variableKey === key));
    const pairIsBroken = pairIsConfigured && pairRows.some(total => !totalResolutions.get(total.id)?.resolved);

    const hiddenTotalIds: string[] = [];
    const totals = attrs.totals.reduce<Record<string, string>>((values, total) => {
        const resolution = totalResolutions.get(total.id);
        const dropsForPair = pairIsBroken && pairKeys.includes(total.variableKey);

        if (total.hideWhenUnresolved && (!resolution?.resolved || dropsForPair)) {
            hiddenTotalIds.push(total.id);

            return values;
        }

        values[total.id] = resolution?.value ?? "";

        return values;
    }, {});

    const inclusiveRowIndexes = rows.reduce<number[]>((indexes, row, index) => {
        if (row.inclusive) indexes.push(index);

        return indexes;
    }, []);

    return {
        ...node,
        attrs: {
            ...node.attrs,
            resolved: {
                groups: attrs.groupByDate ? buildResolvedFolioGroups(rows) : null,
                hiddenColumnIds: hiddenColumnIds.length ? hiddenColumnIds : null,
                hiddenTotalIds: hiddenTotalIds.length ? hiddenTotalIds : null,
                inclusiveRowIndexes: inclusiveRowIndexes.length ? inclusiveRowIndexes : null,
                items: rows.map(row => row.values),
                totals,
            },
        },
    };
};

const resolvePrintoutDataListNode = (
    node: JSONContent,
    context: PrintoutVariableResolveContext,
): JSONContent => {
    const attrs = getDataListAttrs(node.attrs as PrintoutDataListAttrs);
    const rows = getDataListRows(attrs.source, context, attrs);

    const items = rows.map((row) => {
        const rowContext = getDataListRowContext(attrs.source, row, context);

        return attrs.columns.reduce<Record<string, string>>((values, column) => {
            values[column.id] = resolvePrintoutVariableText(
                column.variableKey,
                rowContext,
                column,
            );

            return values;
        }, {});
    });

    return {
        ...node,
        attrs: {
            ...node.attrs,
            resolved: {items},
        },
    };
};

const resolvePrintoutNode = (
    node: JSONContent,
    context: PrintoutVariableResolveContext,
): ResolvedPrintoutNode => {
    if (node.type === "printoutVariable") {
        const reference = getPrintoutVariableReference(node.attrs);
        const resolvedVariable = reference
            ? resolvePrintoutVariable(
                reference,
                context,
                getPrintoutVariableFormatting(node.attrs),
            )
            : undefined;
        const variable = resolvedVariable?.variable ?? (reference ? findPrintoutVariable(reference.split(":")[0]) : undefined);
        const text = resolvedVariable?.displayValue ?? "";

        if (text) {
            const marks = getResolvedPrintoutVariableMarks(node.marks, resolvedVariable);

            return {
                ...(marks.length ? {marks} : {}),
                text,
                type: "text",
            };
        }

        if (variable) {
            const fallback = normalizePrintoutVariableFallback(node.attrs?.fallback, variable);

            if (fallback !== "") {
                const marks = getResolvedPrintoutVariableMarks(node.marks, resolvedVariable);

                return {
                    ...(marks.length ? {marks} : {}),
                    text: fallback,
                    type: "text",
                };
            }

            return null;
        }

        return {
            ...node,
            attrs: {
                ...node.attrs,
                previewUnresolved: true,
                previewValue: null,
            },
        };
    }

    if (node.type === "printoutDataList") {
        return resolvePrintoutDataListNode(node, context);
    }

    if (node.type === "printoutFolioTable") {
        return resolvePrintoutFolioTableNode(node, context);
    }

    const nextNode: JSONContent = {...node};

    if (node.content) {
        const nextContent = node.content
            .flatMap((child) => {
                const resolvedChild = resolvePrintoutNode(child, context);
                if (!resolvedChild) return [];

                return Array.isArray(resolvedChild) ? resolvedChild : [resolvedChild];
            });

        if (nextContent.length) {
            nextNode.content = nextContent;
        } else {
            delete nextNode.content;
        }
    }

    return nextNode;
};

export const resolvePrintoutContent = (
    content: JSONContent,
    context: PrintoutVariableResolveContext,
) => {
    const resolvedContent = resolvePrintoutNode(content, context);
    if (!resolvedContent) return EMPTY_DOCUMENT;
    if (Array.isArray(resolvedContent)) {
        return {
            ...EMPTY_DOCUMENT,
            content: resolvedContent,
        };
    }

    return resolvedContent;
};
